import { useState } from "react";
import { ArrowRight, Building2, GraduationCap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { login } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

export default function LoginPage({ onLogin }) {
  const [role, setRole] = useState("professor");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login({ ...form, role });
      onLogin({ ...result.user, session_token: result.session_token });
    } catch (err) {
      setError(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  const switchRole = (value) => {
    setRole(value);
    setForm({ email: "", password: "" });
  };

  return (
    <main className="min-h-screen bg-white" data-testid="login-page">
      <section className="grid min-h-screen grid-cols-1 lg:grid-cols-[1.08fr_.92fr]">
        <div className="relative flex min-h-[44vh] items-end overflow-hidden bg-zinc-950 p-6 md:p-10 lg:min-h-screen" data-testid="login-hero-section">
          <img src={HERO} alt="Équipe en entreprise" className="absolute inset-0 h-full w-full object-cover opacity-72" data-testid="login-hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="relative max-w-3xl stagger-in">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-white/75" data-testid="login-kicker">FilStage</p>
            <h1 className="font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="login-title">Présences de stage confirmées par QR code.</h1>
            <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg" data-testid="login-subtitle">Un espace partagé entre professeurs et tuteurs pour suivre, confirmer et historiser les présences des étudiants BTS en entreprise.</p>
          </div>
        </div>
        <div className="flex items-center justify-center p-6 md:p-10">
          <div className="w-full max-w-md border border-zinc-200 bg-white p-6 md:p-8 stagger-in" style={{ animationDelay: "120ms" }} data-testid="login-panel">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#002FA7]" data-testid="login-access-label">Accès sécurisé</p>
                <h2 className="font-display text-3xl font-bold" data-testid="login-panel-title">Connexion</h2>
              </div>
              <ShieldCheck className="h-9 w-9 text-[#002FA7]" aria-hidden="true" />
            </div>
            <Tabs value={role} onValueChange={switchRole} className="w-full" data-testid="login-role-tabs">
              <TabsList className="grid h-12 w-full grid-cols-2 rounded-none border bg-zinc-50 p-0" data-testid="login-role-tabs-list">
                <TabsTrigger value="professor" className="h-full rounded-none data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="login-professor-tab"><GraduationCap className="h-4 w-4" />Professeur</TabsTrigger>
                <TabsTrigger value="tutor" className="h-full rounded-none data-[state=active]:bg-[#002FA7] data-[state=active]:text-white" data-testid="login-tutor-tab"><Building2 className="h-4 w-4" />Tuteur</TabsTrigger>
              </TabsList>
              <TabsContent value={role} className="mt-6" data-testid="login-form-tab-content">
                <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="email" data-testid="login-email-label">Email</Label>
                    <Input id="email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="h-12 rounded-none" data-testid="login-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" data-testid="login-password-label">Mot de passe</Label>
                    <Input id="password" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} className="h-12 rounded-none" data-testid="login-password-input" />
                  </div>
                  {error && <p className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="login-error-message">{error}</p>}
                  <Button type="submit" disabled={loading} className="h-12 w-full rounded-none bg-[#002FA7] transition-[transform,box-shadow,background-color] hover:-translate-y-px hover:bg-[#012777]" data-testid="login-submit-button">
                    {loading ? "Connexion…" : "Entrer dans l’application"}<ArrowRight className="h-4 w-4" />
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            <p className="mt-5 text-sm text-zinc-500" data-testid="login-demo-hint">Renseignez les identifiants de votre environnement (comptes créés à l'initialisation du serveur).</p>
          </div>
        </div>
      </section>
    </main>
  );
}